import {
  createContext, useContext, useRef, useState, useCallback, useMemo,
} from 'react';
import { TranscriptionService } from '@klangio/ai2notes-ts-client';
import * as React from 'react';
import { TranscriptionState } from '../../entities';

interface JobPollingState {
  processing: boolean;
  message: string;
  progress: number;
  estimatedDuration: number;
  processingTime: number;
  finishedData: any | null;
  error: Error | null;
}

interface ProcessingContextValue {
  jobs: Record<string, JobPollingState>;
  startPolling: (jobId: string) => void;
  stopPolling: (jobId: string) => void;
  setEstimatedDuration: (jobId: string, value: number) => void;
}

const ProcessingContext = createContext<ProcessingContextValue>({
  jobs: {},
  startPolling: () => {},
  stopPolling: () => {},
  setEstimatedDuration: () => {},
});

export const ProcessingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [jobs, setJobs] = useState<Record<string, JobPollingState>>({});
  const intervals = useRef<Record<string, any>>({});

  const startPolling = useCallback((jobId: string) => {
    setJobs((prev) => ({
      ...prev,
      [jobId]: {
        processing: true,
        message: '',
        progress: 0,
        estimatedDuration: 60,
        processingTime: 0,
        finishedData: null,
        error: null,
      },
    }));

    if (intervals.current[jobId]) clearInterval(intervals.current[jobId]);

    intervals.current[jobId] = setInterval(async () => {
      try {
        const result = await TranscriptionService.transcriptionGetGet(jobId);
        const isFinished = result.state === TranscriptionState.Ok
          || result.state === TranscriptionState.Error
          || result.state === TranscriptionState.Refunded;

        setJobs((prev) => ({
          ...prev,
          [jobId]: {
            ...prev[jobId],
            progress: result.progress,
            message: result.state, // You can use your getMessageByStateAndQueuePosition here
            processingTime: prev[jobId].processingTime + 1,
            finishedData: isFinished ? { id: result.id, state: result.state } : null,
            processing: !isFinished,
            error: null,
          },
        }));

        if (isFinished) {
          clearInterval(intervals.current[jobId]);
          delete intervals.current[jobId];
        }
      } catch (error) {
        setJobs((prev) => ({
          ...prev,
          [jobId]: {
            ...prev[jobId],
            processing: false,
            error,
          },
        }));
        clearInterval(intervals.current[jobId]);
        delete intervals.current[jobId];
      }
    }, 1000);
  }, []);

  const stopPolling = useCallback((jobId: string) => {
    if (intervals.current[jobId]) {
      clearInterval(intervals.current[jobId]);
      delete intervals.current[jobId];
    }
    setJobs((prev) => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        processing: false,
      },
    }));
  }, []);

  const setEstimatedDuration = useCallback((jobId: string, value: number) => {
    setJobs((prev) => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        estimatedDuration: value,
      },
    }));
  }, []);

  const value = useMemo(
    () => ({
      jobs,
      startPolling,
      stopPolling,
      setEstimatedDuration,
    }),
    [jobs, startPolling, stopPolling, setEstimatedDuration],
  );

  return <ProcessingContext.Provider value={value}>{children}</ProcessingContext.Provider>;
};

export const useProcessing = () => useContext(ProcessingContext);
